import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Target, TrendingUp, TrendingDown, Calendar, DollarSign } from 'lucide-react';
import { Budget, BudgetCategory, Transaction } from '../types';
import { budgetService, budgetCategoryService, enhancedTransactionService } from '../services/database';
import { formatCurrency } from '../utils/helpers';
import { useAuth } from '../hooks/useAuth';
import { ConfirmModal } from '../components/ConfirmModal';

interface BudgetWithProgress extends Budget {
  category: BudgetCategory;
  spent: number;
  remaining: number;
  progress: number;
  isOverBudget: boolean;
}

export const BudgetPage: React.FC = () => {
  const { user } = useAuth();
  const [budgets, setBudgets] = useState<BudgetWithProgress[]>([]);
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddBudget, setShowAddBudget] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const [formData, setFormData] = useState({
    category_id: '',
    amount: '',
    period: 'monthly' as 'monthly' | 'yearly',
    start_date: selectedMonth + '-01',
  });

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [budgetsData, categoriesData, transactionsData] = await Promise.all([
        budgetService.getBudgets(),
        budgetCategoryService.getBudgetCategories(),
        enhancedTransactionService.getTransactions(),
      ]);

      setCategories(categoriesData);
      setTransactions(transactionsData);

      // Calculate budget progress
      const budgetsWithProgress = budgetsData.map(budget => {
        const category = categoriesData.find(c => c.id === budget.category_id);
        const monthStart = new Date(selectedMonth + '-01');
        const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);

        const monthTransactions = transactionsData.filter(tx => {
          const txDate = new Date(tx.date);
          return tx.type === 'expense' && 
                 txDate >= monthStart && 
                 txDate <= monthEnd &&
                 tx.category === category?.name;
        });

        const spent = monthTransactions.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
        const remaining = budget.amount - spent;
        const progress = Math.min((spent / budget.amount) * 100, 100);
        const isOverBudget = spent > budget.amount;

        return {
          ...budget,
          category: category!,
          spent,
          remaining,
          progress,
          isOverBudget,
        };
      });

      setBudgets(budgetsWithProgress);
    } catch (error) {
      console.error('Error loading budget data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category_id || !formData.amount) return;

    try {
      const budgetData = {
        user_id: user?.id || '00000000-0000-0000-0000-000000000001', // Use authenticated user ID
        category_id: formData.category_id,
        amount: parseFloat(formData.amount),
        period: formData.period,
        start_date: formData.start_date,
        is_active: true,
        currency: 'USD',
      };

      if (editingBudget) {
        await budgetService.updateBudget(editingBudget.id, budgetData);
      } else {
        await budgetService.createBudget(budgetData);
      }

      setFormData({
        category_id: '',
        amount: '',
        period: 'monthly',
        start_date: selectedMonth + '-01',
      });
      setShowAddBudget(false);
      setEditingBudget(null);
      loadData();
    } catch (error) {
      console.error('Error saving budget:', error);
    }
  };

  const handleDelete = async (budgetId: string) => {
    setConfirmDeleteId(budgetId);
  };
  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await budgetService.deleteBudget(confirmDeleteId);
      loadData();
    } catch (error) {
      console.error('Error deleting budget:', error);
    } finally {
      setConfirmDeleteId(null);
    }
  };
  const cancelDelete = () => setConfirmDeleteId(null);

  const handleEdit = (budget: Budget) => {
    setEditingBudget(budget);
    setFormData({
      category_id: budget.category_id,
      amount: budget.amount.toString(),
      period: budget.period,
      start_date: budget.start_date,
    });
    setShowAddBudget(true);
  };

  const getProgressColor = (progress: number, isOverBudget: boolean) => {
    if (isOverBudget) return 'bg-red-500';
    if (progress >= 90) return 'bg-yellow-500';
    if (progress >= 75) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getProgressBgColor = (progress: number, isOverBudget: boolean) => {
    if (isOverBudget) return 'bg-red-100';
    if (progress >= 90) return 'bg-yellow-100';
    if (progress >= 75) return 'bg-orange-100';
    return 'bg-green-100';
  };

  const totalBudget = budgets.reduce((sum, budget) => sum + budget.amount, 0);
  const totalSpent = budgets.reduce((sum, budget) => sum + budget.spent, 0);
  const totalRemaining = totalBudget - totalSpent;
  const overallProgress = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Budget</h1>
          <p className="text-gray-600 dark:text-gray-400">Track your spending and stay on budget</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            {Array.from({ length: 12 }, (_, i) => {
              const date = new Date();
              date.setMonth(date.getMonth() - i);
              const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
              const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
              return { value, label };
            }).map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowAddBudget(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Budget
          </button>
        </div>
      </div>

      {/* Overall Budget Summary */}
      <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalBudget)}</div>
            <div className="text-sm text-gray-600">Total Budget</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalSpent)}</div>
            <div className="text-sm text-gray-600">Total Spent</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${totalRemaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(Math.abs(totalRemaining))}
            </div>
            <div className="text-sm text-gray-600">
              {totalRemaining >= 0 ? 'Remaining' : 'Over Budget'}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Progress</span>
            <span>{overallProgress.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                overallProgress > 100 ? 'bg-red-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(overallProgress, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Budget Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {budgets.map((budget) => (
          <div key={budget.id} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ backgroundColor: budget.category.color }}
                >
                  {budget.category.icon || budget.category.name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{budget.category.name}</h3>
                  <p className="text-sm text-gray-600">{budget.period}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(budget)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(budget.id)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Budget</span>
                <span className="font-semibold text-gray-900">{formatCurrency(budget.amount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Spent</span>
                <span className="font-semibold text-gray-900">{formatCurrency(budget.spent)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Remaining</span>
                <span className={`font-semibold ${budget.remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(budget.remaining))}
                </span>
              </div>

              <div className="pt-2">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{budget.progress.toFixed(1)}%</span>
                </div>
                <div className={`w-full rounded-full h-2 ${getProgressBgColor(budget.progress, budget.isOverBudget)}`}>
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(budget.progress, budget.isOverBudget)}`}
                    style={{ width: `${Math.min(budget.progress, 100)}%` }}
                  ></div>
                </div>
              </div>

              {budget.isOverBudget && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <TrendingUp className="w-4 h-4" />
                  <span>Over budget by {formatCurrency(Math.abs(budget.remaining))}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Budget Modal */}
      {showAddBudget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingBudget ? 'Edit Budget' : 'Add Budget'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period
                </label>
                <select
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value as 'monthly' | 'yearly' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  {editingBudget ? 'Update' : 'Create'} Budget
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddBudget(false);
                    setEditingBudget(null);
                    setFormData({
                      category_id: '',
                      amount: '',
                      period: 'monthly',
                      start_date: selectedMonth + '-01',
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!confirmDeleteId}
        message="Are you sure you want to delete this budget?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}; 